import React from "react";
import {
  BankOutlined,
  CloudUploadOutlined,
  UserOutlined,
  PictureOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Rule } from "antd/es/form";
import { Utils } from "../../Utilities/Utils";
import {
  PortalUrlType,
  EmailProviderType,
  UserCreationMethod,
  LoginMode,
  CsvTemplateOptions,
  CsvTemplateType,
} from "../../Services/PracticeDataRequestService";

export type FieldType =
  | "text"
  | "email"
  | "phone"
  | "url"
  | "radio"
  | "hidden"
  | "info"
  | "subsection"
  | "custom";

export type CustomRenderKey =
  | "logoUpload"
  | "faviconUpload"
  | "invoiceHeaderTemplate"
  | "invoiceFooterTemplate"
  | "dataImportFile";

export interface IFieldOption {
  label: string;
  value: any;
  visibleWhen?: (values: any) => boolean;
}

export interface IFieldConfig {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  placeholder?: string;
  colSpan?: { xs: number; sm: number };
  rules?: Rule[];
  options?: IFieldOption[];
  optionsLayout?: "inline" | "block";
  customKey?: CustomRenderKey;
  visibleWhen?: (values: any) => boolean;
  initialValue?: any;
  helperText?: string;
  addonBefore?: string;
  addonAfter?: string;
  maxLength?: number;
  // Adds extra top spacing to visually separate this field from the previous group.
  groupStart?: boolean;
  // Which data-import template this field's dropzone/preview binds to (customKey: "dataImportFile").
  fileTemplateType?: CsvTemplateType;
}

export interface ISectionConfig {
  key: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  required?: boolean;
  fields?: IFieldConfig[];
}

export interface IStepConfig {
  id: number;
  key: string;
  label: string;
  icon: React.ReactNode;
  sections: ISectionConfig[];
}

const phoneRule: Rule = {
  validator: Utils.ValidateUkPhoneNumber,
};

const isMicrosoftProvider = (values: any) =>
  values.emailProvider === EmailProviderType.MicrosoftOutlook;

// UI-only toggle (not a backend enum) driving visibility of customNoReplyEmail.
export enum NoReplyEmailOption {
  Default = "Default",
  Custom = "Custom",
}

export const DEFAULT_NOREPLY_EMAIL = "noreply@app.actingoffice.com";

export const DEFAULT_REQUIRED_CSV_TEMPLATES: CsvTemplateType[] = [
  CsvTemplateType.Businesses,
  CsvTemplateType.Users,
  CsvTemplateType.Contacts,
];

export const getRequiredCsvTemplates = (formValues?: any): CsvTemplateType[] => {
  const method = formValues?.userCreationMethod;
  const isScim =
    method === UserCreationMethod.CreateViaSCIM ||
    method === 2 ||
    method === "CreateViaSCIM";

  if (isScim) {
    return [CsvTemplateType.Businesses, CsvTemplateType.Contacts];
  }
  return [
    CsvTemplateType.Businesses,
    CsvTemplateType.Users,
    CsvTemplateType.Contacts,
  ];
};

export const isTemplateRequired = (
  templateType: CsvTemplateType,
  formValues?: any,
): boolean => {
  return getRequiredCsvTemplates(formValues).includes(templateType);
};

export const FORM_STEPS: IStepConfig[] = [
  {
    id: 1,
    key: "basic",
    label: "Practice & Portal Setup",
    icon: React.createElement(BankOutlined),
    sections: [
      {
        key: "basic_info",
        label: "Basic information",
        required: true,
        icon: React.createElement(BankOutlined),
        description: "",
        fields: [
          {
            name: "practiceName",
            label: "Practice name",
            type: "text",
            required: true,
            placeholder: "Enter practice name",
            colSpan: { xs: 24, sm: 12 },
            rules: [{ validator: Utils.validateWhiteSpace }],
          },
          {
            name: "website",
            label: "Website",
            type: "text",
            required: true,
            placeholder: "Enter website URL",
            colSpan: { xs: 24, sm: 12 },
            rules: [{ validator: Utils.validateWhiteSpace }],
          },
          {
            name: "practicePhoneNumber",
            label: "Phone number",
            type: "phone",
            required: true,
            addonBefore: "+44",
            maxLength: 10,
            placeholder: "Enter phone number",
            colSpan: { xs: 24, sm: 12 },
            rules: [phoneRule],
          },
          {
            name: "publicEmailNoReply",
            label: "Email",
            type: "email",
            required: true,
            placeholder: "Enter email address",
            colSpan: { xs: 24, sm: 12 },
            rules: [{ validator: Utils.ValidateEmailFormat }],
          },
        ],
      },
      {
        key: "contact_person",
        label: "Users and Email",
        required: true,
        icon: React.createElement(UserOutlined),
        description: "",
        fields: [
          {
            name: "sub_primary_contact",
            label: "Primary Contact Information",
            type: "subsection",
            colSpan: { xs: 24, sm: 24 },
          },
          {
            name: "contactPersonName",
            label: "Name",
            type: "text",
            required: true,
            placeholder: "Enter contact person name",
            colSpan: { xs: 24, sm: 12 },
          },
          {
            name: "contactPersonEmail",
            label: "Email",
            type: "email",
            required: true,
            placeholder: "Enter contact email address",
            colSpan: { xs: 24, sm: 12 },
            rules: [{ validator: Utils.ValidateEmailFormat }],
          },
          {
            name: "contactPersonPhone",
            label: "Phone",
            type: "phone",
            required: true,
            addonBefore: "+44",
            maxLength: 10,
            placeholder: "Enter contact phone number",
            colSpan: { xs: 24, sm: 12 },
            rules: [phoneRule],
          },
          {
            name: "sub_email_domain",
            label: "Email & Workspace URL Setup",
            type: "subsection",
            colSpan: { xs: 24, sm: 24 },
            groupStart: true,
          },
          {
            name: "noReplyEmailOption",
            label: "No-reply Email Address",
            type: "radio",
            initialValue: NoReplyEmailOption.Default,
            colSpan: { xs: 24, sm: 24 },
            optionsLayout: "inline",
            options: [
              { label: "Use default no-reply address", value: NoReplyEmailOption.Default },
              { label: "Use a custom email address", value: NoReplyEmailOption.Custom },
            ],
          },
          {
            name: "noReplyEmailNotice",
            label: `Your no-reply address will be ${DEFAULT_NOREPLY_EMAIL}.`,
            type: "info",
            colSpan: { xs: 24, sm: 24 },
            visibleWhen: (values) =>
              (values.noReplyEmailOption || NoReplyEmailOption.Default) ===
              NoReplyEmailOption.Default,
          },
          {
            name: "customNoReplyEmail",
            label: "Custom Email Address",
            type: "email",
            required: true,
            placeholder: "Enter custom no-reply email address",
            colSpan: { xs: 24, sm: 12 },
            rules: [{ validator: Utils.ValidateEmailFormat }],
            helperText:
              "DNS settings for SendGrid authentication will be shared by Acting Office separately.",
            visibleWhen: (values) =>
              values.noReplyEmailOption === NoReplyEmailOption.Custom,
          },
          {
            name: "portalUrlOption",
            label: "Preferred Portal URL Choice",
            type: "radio",
            initialValue: PortalUrlType.Subdomain,
            colSpan: { xs: 24, sm: 24 },
            optionsLayout: "inline",
            groupStart: true,
            options: [
              { label: "Subdomain (actingoffice.com)", value: PortalUrlType.Subdomain },
              { label: "Custom Domain (.com)", value: PortalUrlType.Custom },
            ],
          },
          {
            name: "proposedUrl",
            label: "Proposed Subdomain",
            type: "url",
            required: true,
            placeholder: "Enter proposed subdomain",
            addonBefore: "https://",
            addonAfter: ".actingoffice.com",
            colSpan: { xs: 24, sm: 12 },
            visibleWhen: (values) =>
              (values.portalUrlOption || PortalUrlType.Subdomain) ===
              PortalUrlType.Subdomain,
          },
          {
            name: "customPortalUrl",
            label: "Custom Portal URL",
            type: "url",
            required: true,
            placeholder: "Enter custom portal URL",
            addonBefore: "https://",
            addonAfter: ".com",
            colSpan: { xs: 24, sm: 12 },
            visibleWhen: (values) =>
              values.portalUrlOption === PortalUrlType.Custom,
          },
          {
            name: "sub_auth_users",
            label: "Authentication & User Provisioning",
            type: "subsection",
            colSpan: { xs: 24, sm: 24 },
            groupStart: true,
          },
          {
            name: "emailProvider",
            label: "Email Provider",
            type: "radio",
            initialValue: EmailProviderType.MicrosoftOutlook,
            colSpan: { xs: 24, sm: 24 },
            optionsLayout: "inline",
            options: [
              { label: "Microsoft / Outlook", value: EmailProviderType.MicrosoftOutlook },
              { label: "Google", value: EmailProviderType.Google },
              { label: "Other", value: EmailProviderType.Other },
            ],
          },
          {
            name: "userCreationMethod",
            label: "User Creation",
            type: "radio",
            initialValue: UserCreationMethod.UploadCsv,
            colSpan: { xs: 24, sm: 12 },
            groupStart: true,
            options: [
              { label: "Upload CSV", value: UserCreationMethod.UploadCsv },
              {
                label: "Create via SCIM",
                value: UserCreationMethod.CreateViaSCIM,
                visibleWhen: isMicrosoftProvider,
              },
            ],
          },
        ],
      },
      {
        key: "branding_assets",
        label: "Branding Assets",
        required: true,
        icon: React.createElement(PictureOutlined),
        description: "",
        fields: [
          {
            name: "sub_logo_icon",
            label: "Logo & Icon",
            type: "subsection",
            colSpan: { xs: 24, sm: 24 },
          },
          {
            name: "logoUrl",
            label: "Company Logo",
            type: "custom",
            required: true,
            customKey: "logoUpload",
            colSpan: { xs: 24, sm: 12 },
          },
          {
            name: "faviconUrl",
            label: "Favicon (Website Icon)",
            type: "custom",
            required: true,
            customKey: "faviconUpload",
            colSpan: { xs: 24, sm: 12 },
          },
          {
            name: "sub_invoice_templates",
            label: "Invoice Templates",
            type: "subsection",
            colSpan: { xs: 24, sm: 24 },
            groupStart: true,
          },
          {
            name: "invoiceSampleHeaderUrl",
            label: "Invoice Header",
            type: "custom",
            required: true,
            customKey: "invoiceHeaderTemplate",
            colSpan: { xs: 24, sm: 12 },
          },
          {
            name: "invoiceSampleFooterUrl",
            label: "Invoice Footer",
            type: "custom",
            required: true,
            customKey: "invoiceFooterTemplate",
            colSpan: { xs: 24, sm: 12 },
          },
        ],
      },
    ],
  },
  {
    id: 2,
    key: "data",
    label: "Data Imports & Templates",
    icon: React.createElement(CloudUploadOutlined),
    // One nav step per data-import template, generated from the canonical CsvTemplateOptions list.
    sections: CsvTemplateOptions.map((opt) => ({
      key: `data_${opt.value}`,
      label: opt.label,
      required: DEFAULT_REQUIRED_CSV_TEMPLATES.includes(opt.value),
      icon: React.createElement(CloudUploadOutlined),
      description: "",
      fields: [
        {
          name: `dataImport_${opt.value}`,
          label: opt.label,
          type: "custom" as const,
          required: DEFAULT_REQUIRED_CSV_TEMPLATES.includes(opt.value),
          customKey: "dataImportFile" as const,
          fileTemplateType: opt.value,
          colSpan: { xs: 24, sm: 24 },
        },
      ],
    })),
  },
];

// Flat lookup helpers - single source of truth for validation/navigation/completion.
export const ALL_SECTIONS: ISectionConfig[] = FORM_STEPS.flatMap(
  (step) => step.sections,
);

export const getSectionConfig = (
  sectionKey: string,
): ISectionConfig | undefined =>
  ALL_SECTIONS.find((section) => section.key === sectionKey);

export const getRequiredFieldNames = (
  sectionKey: string,
  formValues?: any,
): string[] => {
  const section = getSectionConfig(sectionKey);
  if (!section || !section.fields) return [];

  return section.fields
    .filter((f) => {
      if (!f.required) return false;
      if (f.visibleWhen && formValues) {
        return f.visibleWhen(formValues);
      }
      return true;
    })
    .map((f) => f.name);
};

export const getAllFieldNames = (sectionKey: string): string[] =>
  getSectionConfig(sectionKey)?.fields?.map((f) => f.name) ?? [];
